import type { ComponentContentSchema } from '@vibesites/runtime';
import {
  FloorplansList as BaseFloorplansList,
  contentSchema as baseSchema,
} from '@vibesites/theme-via6/components/FloorplansList';

/**
 * The Majestic — FloorplansList override (home-page floor-plan cards).
 *
 * Customer feedback: the parent's hover kept the same look they like — a
 * semi-transparent primary tint over the card plus a centered "View
 * Plans" pill — but revealed it by iris-expanding a gray square via a
 * slow clip-path animation, which felt clunky. Requested change: keep
 * the tint + pill, drop the expanding square, and just fade it in fast.
 *
 * Done as a pure CSS style-wrapper (same approach as AmenitiesGrid) so
 * we don't fork the parent's markup, slot definitions, or floorplans
 * binding. We reuse the parent's existing `.fp-card-overlay` (tint) and
 * `.fp-card-cta-hover` (pill) elements and only change how they appear:
 *
 *   1. `.fp-card-overlay` — pin the clip-path fully open at all times so
 *      it never irises, and instead drive visibility with a fast opacity
 *      fade (0 → 1 on hover). The parent's primary tint background is
 *      kept untouched, so the color/coverage reads exactly as today.
 *   2. `.fp-card-cta-hover` — strip the parent's translateY slide and
 *      its 0.35s entrance delay so the pill cross-fades in together with
 *      the tint instead of trailing behind it.
 *
 * Everything is scoped to `[data-vibesite-slot="FloorplansList"]` (the
 * stable attribute the editable section wrapper carries) and each
 * selector out-specifies the parent's single-class rules, so the
 * override wins by specificity without `!important`. The parent's
 * edit-mode pointer-events handling is left intact. A
 * prefers-reduced-motion guard drops the fade transitions.
 *
 * The base schema travels with us via the re-attach below so the editor
 * still surfaces the same card-image and CTA controls.
 */
export function FloorplansList() {
  return (
    <>
      <BaseFloorplansList />
      <style>{`
        /* Tint — no more iris. Keep the overlay fully covering the card
           at all times and reveal it with a quick opacity fade instead
           of the clip-path expansion. Recolored from the parent's primary
           tint to the theme secondary (same 65% translucency). */
        [data-vibesite-slot="FloorplansList"] .fp-card-overlay {
          background: color-mix(in srgb, var(--vibe-color-secondary) 65%, transparent);
          clip-path: inset(0% 0% 0% 0%);
          -webkit-clip-path: inset(0% 0% 0% 0%);
          opacity: 0;
          transition: opacity 0.18s ease;
        }
        [data-vibesite-slot="FloorplansList"] .fp-card:hover .fp-card-overlay,
        [data-vibesite-slot="FloorplansList"] .fp-card:focus-visible .fp-card-overlay {
          clip-path: inset(0% 0% 0% 0%);
          -webkit-clip-path: inset(0% 0% 0% 0%);
          opacity: 1;
        }

        /* CTA pill — drop the parent's slide + 0.35s delay so it simply
           cross-fades in with the tint. Text/border switch to
           text-on-secondary so the pill stays legible against the now
           secondary-colored tint. */
        [data-vibesite-slot="FloorplansList"] .fp-card-cta-hover {
          transform: none;
          color: var(--vibe-color-text-on-secondary);
          border-color: color-mix(in srgb, var(--vibe-color-text-on-secondary) 75%, transparent);
          transition: opacity 0.18s ease,
                      border-color 0.2s ease,
                      background-color 0.2s ease,
                      color 0.2s ease;
        }
        [data-vibesite-slot="FloorplansList"] .fp-card:hover .fp-card-cta-hover,
        [data-vibesite-slot="FloorplansList"] .fp-card:focus-visible .fp-card-cta-hover {
          transform: none;
          border-color: var(--vibe-color-text-on-secondary);
        }

        @media (prefers-reduced-motion: reduce) {
          [data-vibesite-slot="FloorplansList"] .fp-card-overlay,
          [data-vibesite-slot="FloorplansList"] .fp-card-cta-hover {
            transition: none;
          }
        }
      `}</style>
    </>
  );
}

(FloorplansList as unknown as { contentSchema: ComponentContentSchema }).contentSchema =
  baseSchema;

export default FloorplansList;
